import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import {
  MagnifyingGlassIcon,
  AdjustmentsHorizontalIcon,
  XMarkIcon,
  BookmarkIcon,
  EyeIcon,
  CalendarIcon,
  TagIcon,
  FolderIcon
} from '@heroicons/react/24/outline';

import { diaryService } from '../../services/diary';
import { format } from 'date-fns';

const SearchResults = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  
  const [searchParams, setSearchParams] = useState({
    q: '',
    tag: '',
    mood: '',
    folder: '',
    page: 1,
    limit: 12
  });

  const [availableFilters, setAvailableFilters] = useState({
    tags: [],
    folders: [],
    moods: ['😊', '😢', '😡', '😴', '🤔', '😍', '😰', '😌']
  });

  // Parse URL parameters on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const newParams = {
      q: urlParams.get('q') || '',
      tag: urlParams.get('tag') || '',
      mood: urlParams.get('mood') || '',
      folder: urlParams.get('folder') || '',
      page: parseInt(urlParams.get('page')) || 1,
      limit: 12
    };
    setSearchParams(newParams);
  }, [location.search]);

  // Fetch search results when parameters change
  useEffect(() => {
    if (searchParams.q || searchParams.tag || searchParams.mood || searchParams.folder) {
      performSearch();
    }
    fetchFilterOptions();
  }, [searchParams]);

  const performSearch = async () => {
    setLoading(true);
    try {
      const response = await diaryService.searchEntries(searchParams);
      setResults(response.data.entries);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Search failed:', error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const [tags, folders] = await Promise.all([
        diaryService.getTags(),
        diaryService.getFolders()
      ]);
      setAvailableFilters(prev => ({ ...prev, tags, folders }));
    } catch (error) {
      console.error('Failed to fetch filter options:', error);
    }
  };

  const updateSearchParams = (key, value) => {
    const newParams = { ...searchParams, [key]: value, page: 1 };
    setSearchParams(newParams);
    
    // Update URL
    const urlParams = new URLSearchParams();
    Object.entries(newParams).forEach(([k, v]) => {
      if (v && k !== 'limit') urlParams.set(k, v);
    });
    navigate(`/search?${urlParams.toString()}`, { replace: true });
  };

  const clearFilters = () => {
    setSearchParams({ q: '', tag: '', mood: '', folder: '', page: 1, limit: 12 });
    navigate('/search', { replace: true });
    setResults([]);
  };

  const getMoodEmoji = (mood) => {
    const moodMap = {
      'happy': '😊', 'sad': '😢', 'excited': '😍', 'angry': '😡',
      'peaceful': '😌', 'anxious': '😰', 'thoughtful': '🤔', 'tired': '😴'
    };
    return moodMap[mood?.toLowerCase()] || mood || '📝';
  };

  const highlightText = (text, query) => {
    if (!query) return text;
    const regex = new RegExp(`(${query})`, 'gi');
    return text.replace(regex, '<mark class="bg-yellow-200">$1</mark>');
  };

  return (
    <div className="min-h-screen bg-paper-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-500 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl font-serif font-bold mb-6">Search Your Journal</h1>
            
            {/* Search Bar */}
            <div className="relative max-w-2xl">
              <MagnifyingGlassIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-blue-300" />
              <input
                type="text"
                placeholder="Search your thoughts, memories, and experiences..."
                value={searchParams.q}
                onChange={(e) => updateSearchParams('q', e.target.value)}
                className="w-full pl-12 pr-4 py-4 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-4 focus:ring-blue-200"
              />
            </div>
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <motion.div
          className="mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              {results.length > 0 && `${pagination?.totalEntries || 0} results found`}
            </h2>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <AdjustmentsHorizontalIcon className="h-5 w-5" />
              <span>Filters</span>
            </button>
          </div>

          {/* Active Filters */}
          {(searchParams.tag || searchParams.mood || searchParams.folder) && (
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <span className="text-sm text-gray-600">Active filters:</span>
              {searchParams.tag && (
                <motion.span
                  className="inline-flex items-center bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                >
                  #{searchParams.tag}
                  <button
                    onClick={() => updateSearchParams('tag', '')}
                    className="ml-2 hover:text-blue-900"
                  >
                    <XMarkIcon className="h-3 w-3" />
                  </button>
                </motion.span>
              )}
              {searchParams.mood && (
                <motion.span
                  className="inline-flex items-center bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                >
                  {searchParams.mood}
                  <button
                    onClick={() => updateSearchParams('mood', '')}
                    className="ml-2 hover:text-green-900"
                  >
                    <XMarkIcon className="h-3 w-3" />
                  </button>
                </motion.span>
              )}
              {searchParams.folder && (
                <motion.span
                  className="inline-flex items-center bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-sm"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                >
                  📁 {searchParams.folder}
                  <button
                    onClick={() => updateSearchParams('folder', '')}
                    className="ml-2 hover:text-purple-900"
                  >
                    <XMarkIcon className="h-3 w-3" />
                  </button>
                </motion.span>
              )}
              <button
                onClick={clearFilters}
                className="text-sm text-gray-500 hover:text-gray-700 underline"
              >
                Clear all
              </button>
            </div>
          )}

          {/* Filter Panel */}
          <AnimatePresence>
            {showFilters && (
              <motion.div
                className="card p-6"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
              >
                <div className="grid md:grid-cols-3 gap-6">
                  {/* Tags Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Filter by Tag
                    </label>
                    <select
                      value={searchParams.tag}
                      onChange={(e) => updateSearchParams('tag', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">All tags</option>
                      {availableFilters.tags.map(tag => (
                        <option key={tag} value={tag}>#{tag}</option>
                      ))}
                    </select>
                  </div>

                  {/* Mood Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Filter by Mood
                    </label>
                    <div className="grid grid-cols-4 gap-2">
                      {availableFilters.moods.map(mood => (
                        <button
                          key={mood}
                          onClick={() => updateSearchParams('mood', mood === searchParams.mood ? '' : mood)}
                          className={`p-2 rounded-lg border-2 transition-all ${
                            searchParams.mood === mood
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <span className="text-xl">{mood}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Folder Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Filter by Folder
                    </label>
                    <select
                      value={searchParams.folder}
                      onChange={(e) => updateSearchParams('folder', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">All folders</option>
                      {availableFilters.folders.map(folder => (
                        <option key={folder} value={folder}>{folder}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Search Results */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : results.length > 0 ? (
          <>
            <motion.div
              className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              {results.map((entry, index) => (
                <motion.div
                  key={entry._id}
                  className="card p-6 hover:shadow-xl transition-all duration-300"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  whileHover={{ y: -5 }}
                >
                  <Link to={`/entries/${entry._id}`} className="block">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center space-x-2">
                        {entry.mood && (
                          <span className="text-2xl">{getMoodEmoji(entry.mood)}</span>
                        )}
                        {entry.isPinned && (
                          <BookmarkIcon className="h-4 w-4 text-red-500 fill-current" />
                        )}
                      </div>
                      <span className="text-xs text-gray-500">
                        {format(new Date(entry.createdAt), 'MMM d')}
                      </span>
                    </div>

                    <h3 
                      className="text-lg font-serif font-semibold text-ink-black mb-2 line-clamp-2"
                      dangerouslySetInnerHTML={{ 
                        __html: highlightText(entry.title, searchParams.q) 
                      }}
                    />

                    <div 
                      className="text-gray-600 text-sm mb-4 line-clamp-3"
                      dangerouslySetInnerHTML={{ 
                        __html: highlightText(
                          entry.content.replace(/<[^>]*>/g, '').substring(0, 120) + '...',
                          searchParams.q
                        )
                      }}
                    />

                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <div className="flex items-center space-x-3">
                        <div className="flex items-center space-x-1">
                          <EyeIcon className="h-3 w-3" />
                          <span>{entry.viewCount}</span>
                        </div>
                        {entry.folder && (
                          <div className="flex items-center space-x-1">
                            <FolderIcon className="h-3 w-3" />
                            <span>{entry.folder}</span>
                          </div>
                        )}
                      </div>
                      <div className="flex space-x-1">
                        {entry.tags.slice(0, 2).map(tag => (
                          <span
                            key={tag}
                            className={`px-2 py-1 rounded-full text-xs ${
                              tag === searchParams.tag
                                ? 'bg-blue-100 text-blue-700'
                                : 'bg-gray-100 text-gray-600'
                            }`}
                          >
                            #{tag}
                          </span>
                        ))}
                        {entry.tags.length > 2 && (
                          <span className="text-gray-400">+{entry.tags.length - 2}</span>
                        )}
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </motion.div>

            {/* Pagination */}
            {pagination && pagination.totalPages > 1 && (
              <motion.div
                className="flex justify-center space-x-2"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.6 }}
              >
                {pagination.hasPrev && (
                  <button
                    onClick={() => updateSearchParams('page', pagination.currentPage - 1)}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Previous
                  </button>
                )}
                
                {Array.from({ length: Math.min(pagination.totalPages, 5) }, (_, i) => {
                  const page = pagination.currentPage <= 3 
                    ? i + 1 
                    : pagination.currentPage + i - 2;
                  if (page > pagination.totalPages) return null;
                  
                  return (
                    <button
                      key={page}
                      onClick={() => updateSearchParams('page', page)}
                      className={`px-4 py-2 rounded-lg transition-colors ${
                        page === pagination.currentPage
                          ? 'bg-blue-600 text-white'
                          : 'border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  );
                })}
                
                {pagination.hasNext && (
                  <button
                    onClick={() => updateSearchParams('page', pagination.currentPage + 1)}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Next
                  </button>
                )}
              </motion.div>
            )}
          </>
        ) : searchParams.q || searchParams.tag || searchParams.mood || searchParams.folder ? (
          <motion.div
            className="card p-12 text-center"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <div className="text-6xl mb-4">🔍</div>
            <h3 className="text-xl font-semibold text-gray-600 mb-2">
              No results found
            </h3>
            <p className="text-gray-500 mb-6">
              Try adjusting your search terms or filters to find what you're looking for.
            </p>
            <button
              onClick={clearFilters}
              className="btn-secondary"
            >
              Clear Search
            </button>
          </motion.div>
        ) : (
          <motion.div
            className="card p-12 text-center"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <div className="text-6xl mb-4">🔍</div>
            <h3 className="text-xl font-semibold text-gray-600 mb-2">
              Start Searching
            </h3>
            <p className="text-gray-500">
              Enter a search term above to find entries in your journal.
            </p>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default SearchResults;